import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import useBetSlip from '@swf/sports/hooks/useBetSlip';
import useMarketPolling from '@swf/sports/hooks/useMarketPolling';

import haveMarketsChanged from '../utils/haveMarketsChanged';

/**
 * Fetches the opportunities API upon addition/removal from the bet slip
 * or when user logs in or out of the site
 * and updates the widget state with the fetched data.
 * It also initialises polling for market updates on the bet slip.
 */
const useOpportunities = () => {
  const { betSlip } = useBetSlip();
  const singles = useSelector(select.betSlip.singleOpportunities);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const currency = useSelector(select.currency.get);
  const dispatch = useDispatch<Dispatch>();
  const isBetReceiptActive = useSelector(select.betSlip.isBetReceiptActive);

  /**
   * Poll for market updates, check for changes and refetch opportunities
   */
  const marketIds = singles.map(({ runnerId }) => runnerId.marketId);
  useMarketPolling(marketIds, (data) => {
    if (haveMarketsChanged(singles, data)) {
      dispatch.betSlip.opportunitiesRequested();
    }
  });

  /**
   * Refetch opportunities when the user adds a new selection
   * or when user logs in or out of the site
   */
  useEffect(() => {
    if (!isBetReceiptActive) {
      dispatch.betSlip.opportunitiesRequested();
    }
  }, [betSlip, isClientAuthenticated, currency, dispatch.betSlip, isBetReceiptActive]);
};

export default useOpportunities;
